import * as React from 'react';
import { css } from '@emotion/css';
import {
  Affix,
  Button,
  Divider,
  Form,
  notification,
  Popconfirm,
  Tooltip,
  Typography,
} from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { GenericObject } from '../../../utils/types';
import {
  deleteUserAccount,
  getUserInfo,
  updateAuthUserInfo,
} from '../../../api/apis';
import Loader from '../../shared/Loader';
import { getFormattedDate, isJson } from '../../../utils/helper';
import Monaco from '../../shared/MonacoEditor';
import { MONACO_OPTIONS, themeSettingsSchema } from '../../../utils/constants';
import FormItemWrapper from './FormItemWrapper';

export interface IProfileDataProps {
  allowProfileEdit?: Boolean;
  showThemeSettings?: Boolean;
  showSearchSettings?: Boolean;
  editThemeSettings: Boolean;
  editSearchPreferences: Boolean;
  showCloseAccount?: Boolean;
  onProfileFetch?: (userData: GenericObject) => void;
  onProfileSave?: (updatedUserData: GenericObject) => void;
}
const profileDataContainer = css`
  .ant-form-item.--metadata-form-item {
    > .ant-row.ant-form-item-row:first-child {
      width: 100%;
    }
  }
  .ant-form-inline .ant-form-item {
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
    min-width: 200px;
    display: flex;

    > .ant-row.ant-form-item-row {
      flex-direction: column;
      align-items: start;
      flex-wrap: nowrap;
    }

    .ant-form-item-label > label {
      color: #6e6c6c;
      &:after {
        display: none;
      }
    }

    .ant-form-item-control-input-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      & > span:first-child {
        height: 32px;
        line-height: 32px;
      }
    }
  }
  .monaco-wrapper {
    height: 250px !important;
  }

  .delete-account {
    &__button {
      margin-top: 2rem;
    }

    &__warning-para {
      font-size: 1rem;
      max-width: max-content;
      margin-top: 0.5rem;
    }
  }

  .affix-content {
    width: 100%;
    padding: 20px;
    background: white;
    box-sizing: border-box;
    border: 1px solid #e8e8e8;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
    text-align: right;
  }
`;

const THEME_SETTINGS_EDITOR_MODEL_PATH = 'a://b/foo.json';
export default function ProfileData({
  showThemeSettings,
  showSearchSettings,
  showCloseAccount,
  onProfileFetch,
  onProfileSave,
  editThemeSettings,
  editSearchPreferences,
}: IProfileDataProps) {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const userDataRef = React.useRef<GenericObject | null>(null);
  const [form] = Form.useForm();

  const fetchUserProfile = async () => {
    setLoading(true);

    try {
      const userData = await getUserInfo();
      const { name, email, picture, user_metadata } = userData;
      userDataRef.current = userData;
      onProfileFetch?.(userData);
      form.setFieldsValue({
        name: name,
        email: email,
        picture: picture,
        ...(user_metadata && user_metadata.theme_settings instanceof Object
          ? {
              theme_settings: JSON.stringify(
                user_metadata.theme_settings,
                null,
                4
              ),
            }
          : {}),
        ...(user_metadata && user_metadata.search_preferences instanceof Object
          ? {
              search_preferences: JSON.stringify(
                user_metadata.search_preferences,
                null,
                4
              ),
            }
          : {}),
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Error fetching the profile data!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = () => {
    deleteUserAccount()
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Account deleted Successfully!',
        });

        setTimeout(() => {
          //  redirecting to homepage
          window.location.href = window.location.origin;
        }, 1500);
      })
      .catch(_e => {
        notification.error({
          message: 'Error',
          description: 'Account could not be deleted!',
        });
      });
  };

  const handleSave = () => {
    setIsUpdating(true);
    form
      .validateFields?.()
      .then(values => {
        const { search_preferences, theme_settings } = values;
        const payload = {
          user_metadata: {
            ...(userDataRef?.current?.user_metadata
              ? userDataRef?.current?.user_metadata
              : {}),
            search_preferences: search_preferences
              ? JSON.parse(search_preferences)
              : {},
            theme_settings: theme_settings ? JSON.parse(theme_settings) : {},
          },
        };

        updateAuthUserInfo(payload)
          .then(res => {
            onProfileSave?.(res);
            notification.success({
              message: 'Success',
              description: 'Profile data updated successfully!',
            });
          })
          .catch(e => {
            notification.error({
              message: 'Profile update error',
              description: (
                <p>
                  {e.message}
                  <br />
                  <br />
                  <Tooltip title={e.response.error.message}>
                    <Typography.Paragraph ellipsis>
                      {e.response.error.message}
                    </Typography.Paragraph>
                  </Tooltip>
                </p>
              ),
            });
          })
          .finally(() => {
            setIsUpdating(false);
          });
      })
      .catch((_errorInfo: GenericObject) => {
        console.log('_errorInfo', _errorInfo);
        notification.error({
          message: 'Error',
          description: 'Error updating the profile data!',
        });
        setIsUpdating(false);
      });
  };

  React.useEffect(() => {
    fetchUserProfile();
  }, []);
  return (
    <div className={profileDataContainer}>
      <Loader loading={loading}>
        <>
          <Form style={{ minHeight: '500px' }} form={form} layout="inline">
            <FormItemWrapper
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Name is required!' }]}
              value={Form.useWatch('name', form)}
              validateFields={form.validateFields}
              editable={false}
            />{' '}
            <FormItemWrapper
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  message: 'Email is required!',
                },
                {
                  type: 'email',
                  message: 'Enter a valid Email!',
                },
              ]}
              value={Form.useWatch('email', form)}
              validateFields={form.validateFields}
              editable={false}
            />
            <FormItemWrapper
              name="picture"
              label="Picture URL"
              rules={[
                { required: true, message: 'URL is required!' },
                {
                  type: 'url',
                  message: 'Enter a valid Image URL!',
                },
              ]}
              value={Form.useWatch('picture', form)}
              validateFields={form.validateFields}
              editable={false}
            />
            <FormItemWrapper
              name="created_at"
              label="Signed Up"
              rules={[]}
              editable={false}
              value={getFormattedDate(userDataRef?.current?.created_at)}
            />
            <FormItemWrapper
              name="primary_identity_provider"
              label="Primary Identity Provider"
              rules={[]}
              editable={false}
              value={userDataRef?.current?.identities?.[0].connection}
            />
            {userDataRef?.current?.latest_login && (
              <FormItemWrapper
                name="latest_login"
                label="Latest Login"
                rules={[]}
                editable={false}
                value={userDataRef?.current?.latest_login}
              />
            )}
            <Divider />
            {(showThemeSettings || showSearchSettings) && (
              <h3 style={{ width: '100%' }}>Metadata</h3>
            )}
            {showThemeSettings && (
              <Form.Item
                style={{ minWidth: '100%' }}
                className="--metadata-form-item"
                name={'theme_settings'}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || isJson(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Invalid JSON!'));
                    },
                  },
                ]}
              >
                <h4>Theme Settings</h4>
                <Monaco
                  path={THEME_SETTINGS_EDITOR_MODEL_PATH}
                  defaultValue={form.getFieldValue('theme_settings')}
                  language="json"
                  value={form.getFieldValue('theme_settings')}
                  onChange={value => {
                    form.setFieldsValue({
                      theme_settings: value,
                    });
                  }}
                  theme="vs-dark"
                  options={MONACO_OPTIONS}
                  readOnly={!editThemeSettings}
                  wrapperClass="monaco-wrapper"
                  customizeMonacoInstance={(monaco: any) => {
                    if (monaco) {
                      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                        validate: true,
                        schemaValidation: 'error',
                        schemas: [
                          {
                            uri: 'http://myserver/root-schema.json', // id of the first schema
                            fileMatch: [
                              THEME_SETTINGS_EDITOR_MODEL_PATH.toString(),
                            ], // associate with our model,
                            schema: { ...themeSettingsSchema },
                          },
                        ],
                      });
                    }
                  }}
                />
              </Form.Item>
            )}
            {showSearchSettings && (
              <Form.Item
                style={{ minWidth: '100%', marginTop: '1rem' }}
                className="--metadata-form-item"
                name={'search_preferences'}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || isJson(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Invalid JSON!'));
                    },
                  },
                ]}
              >
                <h4>Search Preferences</h4>
                <Monaco
                  defaultValue={form.getFieldValue('search_preferences')}
                  language="json"
                  value={form.getFieldValue('search_preferences')}
                  onChange={value =>
                    form.setFieldsValue({
                      search_preferences: value,
                    })
                  }
                  theme="vs-dark"
                  options={MONACO_OPTIONS}
                  readOnly={!editSearchPreferences}
                  wrapperClass="monaco-wrapper"
                />
              </Form.Item>
            )}
          </Form>

          {showCloseAccount && (
            <>
              <Popconfirm
                title="Are you sure you want to delete your Account?"
                okText="Yes"
                cancelText="No"
                onConfirm={handleDeleteUser}
              >
                <Button
                  icon={<WarningOutlined />}
                  className="delete-account__button"
                  type="primary"
                  danger
                >
                  Delete Account
                </Button>
              </Popconfirm>
              <Typography.Paragraph className="delete-account__warning-para">
                Closing an account is a permanent action. When you close an
                account, all your profile data will be deleted.
              </Typography.Paragraph>
            </>
          )}
        </>
        <Affix
          offsetBottom={0}
          style={{
            backgroundColor: '#fff',
            padding: '0',
            width: 'calc(100%)',
          }}
        >
          <div className="affix-content">
            <Button type="primary" onClick={handleSave} loading={isUpdating}>
              Save Settings
            </Button>
          </div>
        </Affix>
      </Loader>
    </div>
  );
}
